import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useListNavigationStore } from '@/lib/useListNavigation';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, ChevronDown, ChevronUp, ArrowRight, ArrowUpDown, AlertTriangle,
  CheckCircle, CheckCircle2, Clock, Wrench, Package, X, ClipboardCheck, Monitor, TestTube,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { SignaturePad } from '@/components/SignaturePad';
import { HelpTooltip } from '@/components/HelpTooltip';
import { useOrderComponents } from '@/hooks/useOrderComponents';
import { AssemblyChecklist } from '@/components/AssemblyChecklist';
import { AONotes } from '@/components/AONotes';
import { supabase } from '@/lib/supabase';
import { isAdmin as isAdminRole } from '@/lib/roles';
import { formatDate, cn, businessDays } from '@/lib/utils';
import { getActiveDaysIfUnmanaged } from '@/lib/api/techActivity';
import { useReviewTimeline } from '@/hooks/useReviewTimeline';
import { useOpenPauses, assertPausesReviewed } from '@/lib/api/pauses';
import { PauseReviewList, PausedBadge } from '@/components/BuildPauses';
import { BuildAttribution } from '@/components/BuildAttribution';
import { DemoSetupBanner, DEMO_SETUP_NOTE } from '@/lib/demoSetup';
import { ReviewBuildTimeline } from '@/components/BuildDuration';
import { ChecklistProgress } from '@/components/ChecklistProgress';
import { toast } from 'sonner';
import { writeAuditLog } from '@/lib/audit';
import { notifyOrderAssigned } from '@/lib/notifications';
import { PrintZebraLabels } from '@/components/PrintZebraLabels';
import { getComponentTypeLabel, type ComponentType } from '@/types/database';

// ── Status config ─────────────────────────────────────────────────────────────
import { PIPELINE_STATUSES, type PipelineStatus } from '@/lib/assemblyOrderStatus';
import { nextAsoNumber, startOfWeekISO, splitShortfall, groupPendingParts, type PendingPartLine } from '@/lib/production';
import { allocationDeductions, resolvePostingBom } from '@/lib/orderBom';
import { useCurrentUser } from '@/lib/useCurrentUser';

const STATUS_CONFIG: Record<PipelineStatus, {
  label: string;
  badge: string;
  topBorder: string;
  next: PipelineStatus | null;
  nextLabel: string | null;
}> = {
  pending:         { label: 'Pending',                badge: 'bg-gray-100 text-gray-700',       topBorder: 'border-t-4 border-t-gray-400',    next: 'building',         nextLabel: 'Start Building'               },
  building:        { label: 'Building',               badge: 'bg-blue-100 text-blue-700',       topBorder: 'border-t-4 border-t-blue-400',    next: 'software',         nextLabel: 'Start Software Installation'  },
  software:        { label: 'Software Installation',  badge: 'bg-indigo-100 text-indigo-700',   topBorder: 'border-t-4 border-t-indigo-400',  next: 'testing',          nextLabel: 'Move to Testing'              },
  testing:         { label: 'Testing',                badge: 'bg-amber-100 text-amber-700',     topBorder: 'border-t-4 border-t-amber-400',   next: 'awaiting_review',  nextLabel: 'Submit for Review'            },
  awaiting_review: { label: 'Awaiting Review',        badge: 'bg-purple-100 text-purple-700',   topBorder: 'border-t-4 border-t-purple-400',  next: null,               nextLabel: null                           },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function daysAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
  return days === 0 ? 'Today' : `${days}d ago`;
}



// ── Queue column widths ───────────────────────────────────────────────────────
const QUEUE_COL_WIDTHS_KEY = 'production.queue.colWidths.v1';
const QUEUE_COL_DEFAULTS: Record<string, number> = {
  readiness:        40,
  reservation_code: 110,
  build_sku:        120,
  description:      200,
  progress:         140,
  status:           100,
  date:             90,
  action:           140,
};

// ── Page ──────────────────────────────────────────────────────────────────────
export function ProductionPage() {
  const queryClient = useQueryClient();

  // Section collapse
  const [queueOpen, setQueueOpen]       = useState(true);
  const [pipelineOpen, setPipelineOpen] = useState(true);

  // Search
  const [queueSearch,    setQueueSearch]    = useState('');
  const [pipelineSearch, setPipelineSearch] = useState('');

  // Create Order modal — from a reservation
  const [createModal, setCreateModal] = useState<{
    reservation: any;
    form: { order_number: string; technician_id: string; quantity: number; build_type: string; to_number: string; notes: string };
  } | null>(null);

  // Approve modal
  const [approveModal, setApproveModal] = useState<{ order: any } | null>(null);

  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [orderToReview, setOrderToReview] = useState<any>(null);
  const [showManagerSignoff, setShowManagerSignoff] = useState(false);
  const [reviewNotes, setReviewNotes] = useState<any[]>([]);
  // Stage timeline, judged duration and pauses for the order under review.
  const review = useReviewTimeline(orderToReview);
  const { data: openPauses = [] } = useOpenPauses();

  // Pending parts dialog — opened from the Units Pending pills
  const [pendingPartsView, setPendingPartsView] = useState<'awaiting' | 'toOrder' | null>(null);

  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [orderToView, setOrderToView] = useState<any>(null);
  const [detailTab, setDetailTab] = useState<'components' | 'checklist' | 'notes'>('components');

  // Reservation queue sort
  type QueueSortField = 'reservation_code' | 'build_sku' | 'description' | 'progress' | 'status' | 'date' | 'readiness';
  const [queueSortField, setQueueSortField] = useState<QueueSortField>('readiness');
  const [queueSortOrder, setQueueSortOrder] = useState<'asc' | 'desc'>('asc');

  // Resizable column widths
  const [queueColWidths, setQueueColWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(QUEUE_COL_WIDTHS_KEY) || '{}');
      return { ...QUEUE_COL_DEFAULTS, ...saved };
    } catch {
      return { ...QUEUE_COL_DEFAULTS };
    }
  });

  const startQueueResize = useCallback((key: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = queueColWidths[key] ?? QUEUE_COL_DEFAULTS[key] ?? 100;
    const onMove = (ev: PointerEvent) => {
      const next = Math.max(40, startW + (ev.clientX - startX));
      setQueueColWidths(prev => ({ ...prev, [key]: next }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setQueueColWidths(prev => {
        try { localStorage.setItem(QUEUE_COL_WIDTHS_KEY, JSON.stringify(prev)); } catch {}
        return prev;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [queueColWidths]);

  // Fetch notes when the review dialog opens
  useEffect(() => {
    if (!orderToReview?.id) { setReviewNotes([]); return; }
    const aoId = orderToReview.id;

    // AO notes
    supabase
      .from('assembly_order_notes')
      .select('id, note_text, created_at, author:technicians!created_by(name, alias, role)')
      .eq('ao_id', aoId)
      .order('created_at', { ascending: true })
      .then(({ data }) => setReviewNotes(data ?? []));
  }, [orderToReview?.id]);

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: currentUser } = useCurrentUser();
  const canReview = isAdminRole(currentUser?.role);

  const { data: reservations = [], isLoading: resLoading } = useQuery({
    queryKey: ['production-reservations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reservations')
        .select('id, reservation_code, reservation_date, build_id, build_sku, quantity, quantity_fulfilled, status, notes, created_at, builds:build_id(description)')
        .in('status', ['Pending', 'In Progress', 'Partial'])
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []).map((r: any) => ({ ...r, build_description: r.builds?.description || '' }));
    },
  });

  // Fetch BOM + stock levels for active reservations to determine buildable units
  const reservationIds = reservations.map((r: any) => r.id);
  type Readiness = { awaitingStock: boolean; buildableFromStock: number; needsOrdering: boolean; unitsNeedingOrder: number; hasComponents: boolean };
  const { data: stockReadiness } = useQuery({
    queryKey: ['production-stock-readiness', reservationIds.join(',')],
    queryFn: async () => {
      if (reservationIds.length === 0) return { byReservation: {} as Record<string, Readiness>, partLines: [] as PendingPartLine[] };
      // Use per-reservation allocation data for accurate readiness
      const allRc: { reservation_id: string; component_sku: string; component_type: string | null; quantity: number; allocated_qty: number; expected_qty: number }[] = [];
      for (let i = 0; i < reservationIds.length; i += 50) {
        const { data } = await supabase
          .from('reservation_components')
          .select('reservation_id, component_sku, component_type, quantity, allocated_qty, expected_qty')
          .in('reservation_id', reservationIds.slice(i, i + 50));
        allRc.push(...(data || []));
      }

      // Fetch component-level on_order_qty + expected_qty to verify PO backing
      const allSkus = [...new Set(allRc.map(rc => rc.component_sku))];
      const compStockMap: Record<string, { on_order_qty: number; expected_qty: number; description: string }> = {};
      for (let i = 0; i < allSkus.length; i += 100) {
        const { data: compRows } = await supabase
          .from('components')
          .select('part_number, description, on_order_qty, expected_qty')
          .in('part_number', allSkus.slice(i, i + 100));
        (compRows || []).forEach((c: any) => {
          compStockMap[c.part_number] = { on_order_qty: c.on_order_qty ?? 0, expected_qty: c.expected_qty ?? 0, description: c.description ?? '' };
        });
      }

      const result: Record<string, Readiness> = {};
      const partLines: PendingPartLine[] = [];
      for (const res of reservations) {
        const comps = allRc.filter(rc => rc.reservation_id === res.id);
        if (comps.length === 0) { result[res.id] = { awaitingStock: false, buildableFromStock: 0, needsOrdering: false, unitsNeedingOrder: 0, hasComponents: false }; continue; }
        const unfulfilled = Math.max(0, res.quantity - (res.quantity_fulfilled || 0));

        // Buildable from on-hand stock
        const buildable = Math.min(...comps.map(c => Math.floor((c.allocated_qty ?? 0) / (c.quantity || 1))));

        // For each component, how much of this reservation's expected_qty is backed by a real PO
        const compCoverage = comps.map(c => {
          const cs = compStockMap[c.component_sku];
          const compOnOrder = cs?.on_order_qty ?? 0;
          const compTotalExpected = cs?.expected_qty ?? (c.expected_qty ?? 0);
          const poAvailable = Math.max(0, compOnOrder - (compTotalExpected - (c.expected_qty ?? 0)));
          const poCovered = Math.min(c.expected_qty ?? 0, poAvailable);
          return { ...c, poCovered };
        });

        const fulfillableWithPOs = unfulfilled > 0
          ? Math.min(unfulfilled, Math.min(...compCoverage.map(c => Math.floor(((c.allocated_qty ?? 0) + c.poCovered) / (c.quantity || 1)))))
          : 0;
        const unitsNeedingOrder = Math.max(0, unfulfilled - fulfillableWithPOs);
        const awaitingStock = compCoverage.some(c => c.poCovered > 0);
        result[res.id] = { awaitingStock, buildableFromStock: buildable, needsOrdering: unitsNeedingOrder > 0, unitsNeedingOrder, hasComponents: true };

        for (const c of compCoverage) {
          const { awaiting, toOrder } = splitShortfall({
            unitsNeeded: unfulfilled, perBuild: c.quantity, allocated: c.allocated_qty ?? 0, poCovered: c.poCovered,
          });
          if (awaiting === 0 && toOrder === 0) continue;
          partLines.push({
            sku: c.component_sku,
            description: compStockMap[c.component_sku]?.description ?? '',
            componentType: c.component_type ?? '',
            reservationId: res.id,
            awaiting,
            toOrder,
          });
        }
      }
      return { byReservation: result, partLines };
    },
    enabled: reservationIds.length > 0,
    staleTime: 30_000,
  });

  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['production-orders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assembly_orders')
        .select('id, order_number, build_sku, build_id, reservation_id, technician_id, quantity, build_type, status, to_number, serial_number, serial_numbers, notes, created_at, started_at, completed_at, completed_date, legacy_iir, is_legacy, tech_signature_url, tech_signed_at, tech_signed_by, manager_signature_url, stage_timestamps, checklist_progress, checklist_version, unit_builders, builder_ids, submitted_by, submitted_at')
        .in('status', PIPELINE_STATUSES)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: technicians = [] } = useQuery({
    queryKey: ['technicians-active'],
    queryFn: async () => {
      const { data } = await supabase
        .from('technicians')
        .select('id, name, surname, initials, role')
        .eq('is_active', true)
        .not('role', 'in', '("marketing","manager","retail")')
        .order('name');
      return data ?? [];
    },
  });

  const { data: latestOrderNumber } = useQuery({
    queryKey: ['latest-order-number'],
    queryFn: async () => {
      const { data: aoData } = await supabase
        .from('assembly_orders')
        .select('order_number')
        .order('order_number', { ascending: false })
        .limit(1);
      return aoData?.[0]?.order_number ?? null;
    },
    staleTime: 0,
  });

  const { data: completedCount = 0 } = useQuery({
    queryKey: ['production-completed-week'],
    queryFn: async () => {
      const { count } = await supabase
        .from('assembly_orders')
        .select('id', { count: 'exact', head: true })
        .in('status', ['Posted', 'Completed'])
        .gte('completed_date', startOfWeekISO());
      return count ?? 0;
    },
  });

  // Fetch full order with joins when detail dialog opens
  const { data: enrichedOrder } = useQuery({
    queryKey: ['production-order-detail', orderToView?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assembly_orders')
        .select(`
          *,
          build:builds(id, sku, description, comments),
          technician:technicians!technician_id(name, initials),
          reservation:reservations(id, reservation_code)
        `)
        .eq('id', orderToView!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!orderToView?.id && detailDialogOpen,
  });

  const { data: detailComponents, isLoading: loadingComponents } = useOrderComponents(enrichedOrder);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createOrderMutation = useMutation({
    mutationFn: async (payload: {
      order_number: string; reservation_id: string; build_id: string | null; build_sku: string;
      technician_id: string | null; quantity: number; build_type: string; to_number: string; notes: string;
    }) => {
      const { data, error } = await supabase
        .from('assembly_orders')
        .insert([{
          ...(payload.order_number ? { order_number: payload.order_number } : {}),
          build_id:       payload.build_id || null,
          build_sku:      payload.build_sku,
          reservation_id: payload.reservation_id,
          technician_id:  payload.technician_id || null,
          quantity:       payload.quantity,
          build_type:     payload.build_type || 'Stock',
          status:         'pending',
          to_number:      payload.to_number || null,
          notes:          payload.notes || null,
        }])
        .select('id, order_number')
        .single();
      if (error) throw error;

      // Snapshot reservation components into assembly_order_components
      // (replace any blueprint components auto-inserted by DB trigger)
      if (payload.reservation_id) {
        const { data: resComps } = await supabase
          .from('reservation_components')
          .select('component_sku, component_type, quantity, unit_cost')
          .eq('reservation_id', payload.reservation_id);

        if (resComps?.length) {
          // Clear any trigger-inserted blueprint components first
          await supabase.from('assembly_order_components').delete().eq('ao_id', data.id);

          const aoQty = payload.quantity || 1;
          await supabase.from('assembly_order_components').insert(
            resComps.map((c: any) => ({
              ao_id:             data.id,
              component_sku:     c.component_sku,
              component_type:    c.component_type || '',
              component_qty:     (c.quantity || 1) * aoQty,
              cost_unit_ex_vat:  c.unit_cost ?? 0,
              cost_batch_ex_vat: (c.unit_cost ?? 0) * (c.quantity || 1) * aoQty,
            }))
          );
        } else {
          toast.warning('Reservation has no components — order will fall back to build blueprint. Check reservation integrity.');
        }
      }

      // Mark reservation as In Progress
      if (payload.reservation_id) {
        await supabase.from('reservations')
          .update({ status: 'In Progress' })
          .eq('id', payload.reservation_id)
          .in('status', ['Pending']); // only advance from Pending, don't override Partial
      }

      // Auto-insert setup checklist note for Demo orders
      if (payload.build_type === 'Demo') {
        const checklist = DEMO_SETUP_NOTE;

        await supabase.from('assembly_order_notes').insert({
          ao_id:     data.id,
          note_text: checklist,
          // created_by left null — system-generated
        });
      }

      return data;
    },
    onSuccess: async (data, payload) => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['latest-order-number'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservations-enriched'] });
      queryClient.invalidateQueries({ queryKey: ['reservation'] });
      toast.success(`Assembly order ${data.order_number} created`);
      setCreateModal(null);
      await writeAuditLog({ action: 'assembly_order_created', entity_type: 'assembly_order', entity_id: data.id, entity_label: data.order_number });

      // Notify technician if assigned (fire-and-forget)
      if (payload.technician_id) {
        const tech = technicians.find((t: any) => t.id === payload.technician_id);
        const techName = tech ? `${tech.name}${tech.surname ? ' ' + tech.surname : ''}` : 'Unknown';
        const { data: { user: authUser } } = await supabase.auth.getUser();

        // Fetch build comments so they can be included in the assignment email
        let buildComments = '';
        if (payload.build_id) {
          const { data: buildRow } = await supabase
            .from('builds')
            .select('comments')
            .eq('id', payload.build_id)
            .maybeSingle();
          buildComments = buildRow?.comments || '';
        }

        notifyOrderAssigned(
          {
            subject: `Automated Message - ${data.order_number} Assigned - ${payload.to_number || 'N/A'} - ${techName}`,
            order_number: data.order_number,
            build_sku: payload.build_sku,
            technician_name: techName,
            quantity: payload.quantity,
            build_type: payload.build_type,
            to_number: payload.to_number || '',
            assigned_by: authUser?.email || '',
            date: new Date().toISOString().split('T')[0],
            notes: payload.notes || '',
            build_comments: buildComments,
          },
          payload.technician_id!,
        );
      }
    },
    onError: (e: any) => toast.error(e.message || 'Failed to create order'),
  });

  const advanceStatusMutation = useMutation({
    mutationFn: async ({ orderId, nextStatus, orderNumber, previousStatus }: { orderId: string; nextStatus: PipelineStatus; orderNumber?: string; previousStatus?: string }) => {
      const updates: any = { status: nextStatus };
      if (nextStatus === 'building') updates.started_at = new Date().toISOString();
      const { error } = await supabase.from('assembly_orders').update(updates).eq('id', orderId);
      if (error) throw error;
      return { orderId, orderNumber, previousStatus, nextStatus };
    },
    onSuccess: async (result) => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      toast.success('Status updated');
      await writeAuditLog({
        action: 'assembly_order_status_changed',
        entity_type: 'assembly_order',
        entity_id: result.orderId,
        entity_label: result.orderNumber,
        old_value: result.previousStatus ? { status: result.previousStatus } : null,
        new_value: { status: result.nextStatus },
        notes: `Status changed from ${result.previousStatus || 'unknown'} to ${result.nextStatus}`,
      });
    },
    onError: (e: any) => toast.error(e.message || 'Failed to advance status'),
  });

  const approveOrderMutation = useMutation({
    mutationFn: async ({ order }: { order: any }) => {
      await assertPausesReviewed(order.id);
      const now = new Date();
      const completedDate = now.toISOString().split('T')[0];
      const startedAt     = order.started_at ? new Date(order.started_at) : new Date(order.created_at);

      // For unmanaged techs, only count days they signed in
      const activeDays = order.technician_id
        ? await getActiveDaysIfUnmanaged(order.technician_id, new Date(order.created_at), now)
        : undefined;

      const daysToBuild   = businessDays(startedAt, now, activeDays);
      const daysToProcess = businessDays(new Date(order.created_at), now, activeDays);

      // Mark as Posted in assembly_orders (StockIOPage reads from here).
      // Note: completed_at is intentionally NOT overwritten — it holds the
      // technician's "submitted for review" timestamp (set when the order
      // first moves to awaiting_review). The admin-posting moment is
      // captured separately in posted_date. Overwriting completed_at here
      // used to shift the order's "finish" time to the admin-approval time,
      // breaking the build-timeline upper bound for unmanaged techs.
      const { error } = await supabase
        .from('assembly_orders')
        .update({
          status:          'Posted',
          completed_date:  completedDate,
          posted_date:     now.toISOString(),
          days_to_build:   daysToBuild,
          days_to_process: daysToProcess,
        })
        .eq('id', order.id);
      if (error) throw error;

      // Component list, reconciled with the reservation — see resolvePostingBom.
      const { bom, source: bomSource, diff: bomDiff } = await resolvePostingBom(
        order.id, order.reservation_id, order.quantity || 1,
      );
      if (bomSource === 'refreshed' && bomDiff) {
        writeAuditLog({
          action: 'assembly_order_components_refreshed',
          entity_type: 'assembly_order',
          entity_id: order.id,
          entity_label: order.order_number,
          old_value: { removed: bomDiff.removed },
          new_value: { added: bomDiff.added },
          notes: 'Order copy disagreed with its reservation at posting; posted with the reservation.',
        });
      }

      // Write total cost back to assembly_orders
      const totalCostExVat = bom.reduce((sum, c) => sum + c.cost * c.qty, 0);
      await supabase.from('assembly_orders').update({ total_cost_ex_vat: totalCostExVat }).eq('id', order.id);

      // Deduct from total_qty and reserved_qty (stock physically consumed by build)
      const skus = bom.map(c => c.sku);
      const { data: comps } = await supabase
        .from('components')
        .select('part_number, total_qty, reserved_qty')
        .in('part_number', skus);

      if (comps?.length) {
        const compMap = Object.fromEntries(
          (comps as any[]).map(c => [c.part_number, { total: c.total_qty ?? 0, reserved: c.reserved_qty ?? 0 }])
        );
        await Promise.all(
          bom.map(item => {
            const cur = compMap[item.sku] ?? { total: 0, reserved: 0 };
            return supabase.from('components').update({
              total_qty: Math.max(0, cur.total - item.qty),
              reserved_qty: Math.max(0, cur.reserved - item.qty),
            }).eq('part_number', item.sku);
          })
        );
      }

      // Also deduct from per-reservation allocation
      if (order.reservation_id) {
        const { data: rcRows } = await supabase
          .from('reservation_components')
          .select('id, component_sku, allocated_qty')
          .eq('reservation_id', order.reservation_id);
        for (const u of allocationDeductions(rcRows ?? [], bom)) {
          await supabase.from('reservation_components').update({
            allocated_qty: u.allocated_qty,
          }).eq('id', u.id);
        }
      }

      // Update reservation fulfillment if linked
      if (order.reservation_id) {
        const { data: res } = await supabase
          .from('reservations')
          .select('quantity, quantity_fulfilled')
          .eq('id', order.reservation_id)
          .single();
        if (res) {
          const newFulfilled = (res.quantity_fulfilled || 0) + order.quantity;
          const newStatus = newFulfilled >= res.quantity ? 'Completed' : 'Partial';
          await supabase
            .from('reservations')
            .update({ quantity_fulfilled: newFulfilled, status: newStatus })
            .eq('id', order.reservation_id);
        }
      }

      return order.order_number as string;
    },
    onSuccess: async (orderNumber) => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['production-completed-week'] });
      queryClient.invalidateQueries({ queryKey: ['posted-assembly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservations-enriched'] });
      queryClient.invalidateQueries({ queryKey: ['reservation'] });
      toast.success(`${orderNumber} posted`);
      setApproveModal(null);
      await writeAuditLog({ action: 'assembly_order_posted', entity_type: 'assembly_order', entity_label: orderNumber });
    },
    onError: (e: any) => toast.error(e.message || 'Failed to post order'),
  });

  // ── Derived data ───────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    let unitsPending = 0, unitsReady = 0, unitsAwaiting = 0, unitsNeedOrdering = 0;
    for (const r of reservations as any[]) {
      const unfulfilled = Math.max(0, r.quantity - (r.quantity_fulfilled || 0));
      unitsPending += unfulfilled;
      const sr = stockReadiness?.byReservation[r.id];
      if (sr) {
        const ready = Math.min(unfulfilled, sr.buildableFromStock);
        unitsReady        += ready;
        unitsNeedOrdering += Math.min(unfulfilled, sr.unitsNeedingOrder);
        unitsAwaiting     += Math.max(0, unfulfilled - ready - Math.min(unfulfilled, sr.unitsNeedingOrder));
      } else {
        unitsAwaiting += unfulfilled; // stock data not yet loaded
      }
    }
    return { openReservations: reservations.length, unitsPending, unitsReady, unitsAwaiting, unitsNeedOrdering, ordersInFlight: orders.length, completedCount };
  }, [reservations, orders, completedCount, stockReadiness]);

  const filteredReservations = useMemo(() => {
    // Sum active AO quantities per reservation. Only hide the reservation from the
    // queue if active orders already cover the full quantity (partial coverage = still show).
    const activeQtyByReservation: Record<string, number> = {};
    for (const o of orders as any[]) {
      if (o.reservation_id) {
        activeQtyByReservation[o.reservation_id] = (activeQtyByReservation[o.reservation_id] ?? 0) + (o.quantity || 1);
      }
    }
    let available = reservations
      .filter((r: any) => {
        const fulfilled = r.quantity_fulfilled || 0;
        if (fulfilled >= r.quantity) return false; // fully fulfilled
        const ordered = activeQtyByReservation[r.id] ?? 0;
        return (fulfilled + ordered) < r.quantity; // still has units needing an order
      })
      .map((r: any) => {
        const ordered = activeQtyByReservation[r.id] ?? 0;
        const sr = stockReadiness?.byReservation[r.id] ?? null;
        const fulfilled = r.quantity_fulfilled || 0;
        const stillNeeded = r.quantity - fulfilled - ordered;
        const globalBuildable = sr?.buildableFromStock ?? null;
        // Actionable = min of (units needed, units buildable from physical stock)
        // Don't subtract fulfilled — posted PAOs already consumed physical stock
        const actionable = globalBuildable !== null
          ? Math.max(0, Math.min(stillNeeded, globalBuildable))
          : stillNeeded;
        return {
          ...r,
          _activeOrdered: ordered,
          _awaitingStock: sr?.awaitingStock ?? false,
          _actionable: actionable,
          _needsOrdering: sr?.needsOrdering ?? false,
          _unitsNeedingOrder: sr?.unitsNeedingOrder ?? 0,
        };
      });

    if (queueSearch) {
      const q = queueSearch.toLowerCase();
      available = available.filter((r: any) =>
        r.reservation_code?.toLowerCase().includes(q) || r.build_sku?.toLowerCase().includes(q) || r.build_description?.toLowerCase().includes(q)
      );
    }

    // Readiness rank: 0 = ready from stock, 1 = mixed, 2 = awaiting PO delivery, 3 = needs ordering
    const readinessRank = (r: any): number => {
      if (r._needsOrdering) return 3;
      const stillNeeded = r.quantity - (r.quantity_fulfilled || 0) - (r._activeOrdered || 0);
      if (r._awaitingStock && r._actionable === 0) return 2;
      if (r._awaitingStock && r._actionable > 0 && r._actionable < stillNeeded) return 1;
      return 0;
    };

    // Sort
    available.sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (queueSortField) {
        case 'reservation_code': aVal = a.reservation_code || ''; bVal = b.reservation_code || ''; break;
        case 'build_sku':        aVal = a.build_sku || '';        bVal = b.build_sku || '';        break;
        case 'description':      aVal = a.build_description || ''; bVal = b.build_description || ''; break;
        case 'progress': {
          const aFul = (a.quantity_fulfilled || 0) + (a._activeOrdered || 0);
          const bFul = (b.quantity_fulfilled || 0) + (b._activeOrdered || 0);
          aVal = a.quantity ? aFul / a.quantity : 0;
          bVal = b.quantity ? bFul / b.quantity : 0;
          break;
        }
        case 'status':     aVal = a.status || '';                        bVal = b.status || '';                        break;
        case 'date':       aVal = a.reservation_date || a.created_at || ''; bVal = b.reservation_date || b.created_at || ''; break;
        case 'readiness':  aVal = readinessRank(a);                      bVal = readinessRank(b);                      break;
      }
      if (aVal < bVal) return queueSortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return queueSortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return available;
  }, [reservations, orders, queueSearch, queueSortField, queueSortOrder, stockReadiness]);

  // Register queue IDs for prev/next navigation on the detail page
  useListNavigationStore('production-reservations', filteredReservations.map((r: any) => r.id));

  // Restore row focus when returning from a reservation detail page
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  useEffect(() => {
    const focusId = sessionStorage.getItem('production-focus-id');
    if (!focusId) return;
    sessionStorage.removeItem('production-focus-id');
    const row = rowRefs.current[focusId];
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      row.classList.add('bg-primary/10');
      setTimeout(() => row.classList.remove('bg-primary/10'), 1500);
    }
  }, [filteredReservations]);

  const pipeline = useMemo(() => {
    const q = pipelineSearch.toLowerCase();
    const filtered = q
      ? orders.filter((o: any) => {
          const tech = (technicians as any[]).find(t => t.id === o.technician_id);
          return (
            o.order_number?.toLowerCase().includes(q) ||
            o.build_sku?.toLowerCase().includes(q)    ||
            o.to_number?.toLowerCase().includes(q)    ||
            o.serial_number?.toLowerCase().includes(q) ||
            tech?.name?.toLowerCase().includes(q)     ||
            tech?.initials?.toLowerCase().includes(q)
          );
        })
      : orders;

    const grouped: Record<PipelineStatus, any[]> = {
      pending: [], building: [], software: [], testing: [], awaiting_review: [],
    };
    filtered.forEach((o: any) => {
      const s = o.status as PipelineStatus;
      if (grouped[s]) grouped[s].push(o);
    });
    return grouped;
  }, [orders, pipelineSearch, technicians]);

  const getTech = (id: string | null) =>
    id ? (technicians as any[]).find(t => t.id === id) : null;

  // ── Pending parts (Units Pending pills) ────────────────────────────────────
  const pendingParts = useMemo(
    () => (pendingPartsView && stockReadiness ? groupPendingParts(stockReadiness.partLines, pendingPartsView) : []),
    [pendingPartsView, stockReadiness],
  );

  const reservationById = useMemo(
    () => new Map((reservations as any[]).map(r => [r.id, r])),
    [reservations],
  );

  const inFlightByReservation = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const o of orders as any[]) {
      if (o.reservation_id) (map[o.reservation_id] ??= []).push(o);
    }
    return map;
  }, [orders]);

  // A reservation with no component rows still counts its units as awaiting in
  // the stat, but has no parts to list - name them so the pill adds up.
  const reservationsWithoutComponents = useMemo(
    () => (reservations as any[]).filter(r =>
      stockReadiness?.byReservation[r.id]?.hasComponents === false && r.quantity - (r.quantity_fulfilled || 0) > 0,
    ),
    [reservations, stockReadiness],
  );

  // ── Open Create Order modal ────────────────────────────────────────────────
  const openCreateModal = (reservation: any) => {
    const fulfilled = reservation.quantity_fulfilled || 0;
    const activeOrdered = reservation._activeOrdered || 0;
    const remaining = Math.max(1, reservation.quantity - fulfilled - activeOrdered);
    const actionable = reservation._actionable ?? remaining;
    const qty = Math.max(1, Math.min(remaining, actionable));
    setCreateModal({
      reservation,
      form: { order_number: nextAsoNumber(latestOrderNumber), technician_id: '', quantity: qty, build_type: 'Stock', to_number: 'TO-', notes: '' },
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Production</h1>
        <p className="text-muted-foreground">Assembly pipeline overview</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-muted-foreground">Open Reservations</p>
              <Package className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-blue-600">{stats.openReservations}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-muted-foreground">Units Pending</p>
              <Clock className="h-5 w-5 text-amber-500" />
            </div>
            <p className="text-3xl font-bold text-amber-600">{stats.unitsPending}</p>
            {stats.unitsPending > 0 && (
              <div className="mt-1.5 flex items-center gap-2 text-xs flex-wrap">
                {stats.unitsReady > 0 && (
                  <span className="flex items-center gap-1 whitespace-nowrap text-green-600">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
                    {stats.unitsReady} ready
                  </span>
                )}
                {stats.unitsAwaiting > 0 && (
                  <button
                    type="button"
                    onClick={() => setPendingPartsView('awaiting')}
                    title="Show parts awaiting delivery"
                    className="whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-1.5 leading-[18px] text-amber-700 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                  >
                    {stats.unitsAwaiting} awaiting
                  </button>
                )}
                {stats.unitsNeedOrdering > 0 && (
                  <button
                    type="button"
                    onClick={() => setPendingPartsView('toOrder')}
                    title="Show parts that need ordering"
                    className="whitespace-nowrap rounded-full border border-red-200 bg-red-50 px-1.5 leading-[18px] text-red-700 hover:bg-red-100 hover:border-red-300 transition-colors"
                  >
                    {stats.unitsNeedOrdering} to order
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {[
          { label: 'Orders in Flight',   value: stats.ordersInFlight, icon: <Wrench className="h-5 w-5 text-purple-500" />, color: 'text-purple-600' },
          { label: 'Completed This Week', value: stats.completedCount, icon: <CheckCircle className="h-5 w-5 text-green-500" />, color: 'text-green-600' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-muted-foreground">{s.label}</p>
                {s.icon}
              </div>
              <p className={cn('text-3xl font-bold', s.color)}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Reservations Queue ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setQueueOpen(o => !o)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Reservations Queue <HelpTooltip tipKey="production.queue" />
              <Badge variant="secondary">{filteredReservations.length}</Badge>
            </CardTitle>
            {queueOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CardHeader>

        {queueOpen && (
          <CardContent className="pt-0 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9 pr-9"
                placeholder="Search reservation code or build SKU…"
                value={queueSearch}
                onChange={e => setQueueSearch(e.target.value)}
              />
              {queueSearch && (
                <button onClick={() => setQueueSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {resLoading ? (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : filteredReservations.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No open reservations</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <table className="text-sm table-fixed" style={{ width: Object.values(queueColWidths).reduce((a, b) => a + b, 0), minWidth: '100%' }}>
                  <colgroup>
                    {(['readiness','reservation_code','build_sku','description','progress','status','date','action'] as const).map(k => (
                      <col key={k} style={{ width: queueColWidths[k] }} />
                    ))}
                  </colgroup>
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      {([
                        ['readiness', 'Readiness', 'text-left'],
                        ['reservation_code', 'Code', 'text-left'],
                        ['build_sku', 'Build', 'text-left'],
                        ['description', 'Description', 'text-left'],
                        ['progress', 'Progress', 'text-center'],
                        ['status', 'Status', 'text-left'],
                        ['date', 'Date', 'text-left'],
                      ] as [QueueSortField, string, string][]).map(([field, label, align]) => (
                        <th
                          key={field}
                          className={cn(align, 'relative px-4 py-2 font-medium cursor-pointer hover:bg-muted/70 select-none whitespace-nowrap')}
                          onClick={() => {
                            if (queueSortField === field) setQueueSortOrder(o => o === 'asc' ? 'desc' : 'asc');
                            else { setQueueSortField(field); setQueueSortOrder('asc'); }
                          }}
                        >
                          <div className={cn('flex items-center gap-1 pr-2 overflow-hidden', align === 'text-center' && 'justify-center')}>
                            <span className="truncate">{label}</span>
                            <ArrowUpDown className={cn('h-3 w-3 flex-shrink-0', queueSortField === field ? 'text-primary' : 'text-muted-foreground')} />
                          </div>
                          <span
                            onPointerDown={e => startQueueResize(field, e)}
                            onClick={e => e.stopPropagation()}
                            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60"
                          />
                        </th>
                      ))}
                      <th className="relative px-4 py-2">
                        <span
                          onPointerDown={e => startQueueResize('action', e)}
                          onClick={e => e.stopPropagation()}
                          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredReservations.map((r: any) => {
                      const fulfilled    = r.quantity_fulfilled || 0;
                      const activeOrdered = r._activeOrdered || 0;
                      const stillNeeded  = r.quantity - fulfilled - activeOrdered;
                      const isPartial    = activeOrdered > 0 && stillNeeded > 0;
                      const awaitingStock: boolean = r._awaitingStock;
                      const actionable: number = r._actionable;
                      const needsOrdering: boolean = r._needsOrdering;
                      const unitsNeedingOrder: number = r._unitsNeedingOrder;
                      return (
                        <tr
                          key={r.id}
                          ref={el => { rowRefs.current[r.id] = el; }}
                          className={cn('hover:bg-muted/30 transition-colors', needsOrdering && 'bg-red-50/40', actionable === 0 && (awaitingStock || needsOrdering) && 'opacity-50')}
                        >
                          <td className="px-4 py-3">
                            {needsOrdering
                              ? <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" title="Components need ordering" />
                              : awaitingStock && actionable === 0
                                ? <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" title="Awaiting stock delivery" />
                                : awaitingStock && actionable > 0 && actionable < stillNeeded
                                  ? <span className="inline-block h-2.5 w-2.5 rounded-full bg-yellow-300" title="Mixed — some from stock, some on order" />
                                  : <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" title="Ready to build from stock" />
                            }
                          </td>
                          <td className="px-4 py-3 font-mono font-medium text-xs">
                            <Link
                              to={`/reservations/${r.id}`}
                              className="text-primary hover:underline"
                              onClick={() => sessionStorage.setItem('production-focus-id', r.id)}
                            >{r.reservation_code}</Link>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs font-medium">{r.build_sku}</span>
                          </td>
                          <td className="px-4 py-3 max-w-[180px]">
                            <span className="text-xs text-muted-foreground truncate block" title={r.build_description}>{r.build_description || '—'}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={cn('text-sm font-medium', needsOrdering ? 'text-red-600' : 'text-amber-600')}>
                              {fulfilled + activeOrdered}/{r.quantity}
                            </span>
                            <span className="text-xs text-muted-foreground ml-1">units</span>
                            {activeOrdered > 0 && (
                              <div className="text-xs text-muted-foreground">
                                {activeOrdered} in progress
                              </div>
                            )}
                            {needsOrdering && (
                              <div className="text-xs text-red-600 font-medium flex items-center justify-center gap-0.5 mt-0.5">
                                <AlertTriangle className="h-3 w-3" />
                                {unitsNeedingOrder === 1 ? '1 unit' : `${unitsNeedingOrder} units`} need ordering
                              </div>
                            )}
                            {awaitingStock && !needsOrdering && actionable < stillNeeded && (
                              <div className="text-xs text-amber-600 flex items-center justify-center gap-0.5 mt-0.5">
                                <AlertTriangle className="h-3 w-3" />
                                Awaiting stock
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {isPartial ? (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                                Partial
                              </Badge>
                            ) : (
                              <Badge className="text-xs capitalize">
                                {r.status?.replace(/_/g, ' ')}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {r.reservation_date ? formatDate(r.reservation_date) : formatDate(r.created_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {stillNeeded > 0 && actionable > 0 && (
                              <Button size="sm" onClick={() => openCreateModal(r)}>
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                {actionable < stillNeeded
                                  ? `Order ${actionable} of ${stillNeeded}`
                                  : isPartial ? `Order ${stillNeeded} more` : 'Create Order'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ── Production Pipeline ────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setPipelineOpen(o => !o)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Production Pipeline <HelpTooltip tipKey="production.pipeline" />
              <Badge variant="secondary">{orders.length}</Badge>
            </CardTitle>
            {pipelineOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CardHeader>

        {pipelineOpen && (
          <CardContent className="pt-0 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9 pr-9"
                placeholder="Search order number, build SKU, TO number, technician…"
                value={pipelineSearch}
                onChange={e => setPipelineSearch(e.target.value)}
              />
              {pipelineSearch && (
                <button onClick={() => setPipelineSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {ordersLoading ? (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 items-start">
                {PIPELINE_STATUSES.map(status => {
                  const cfg  = STATUS_CONFIG[status];
                  const cols = pipeline[status] ?? [];
                  return (
                    <div key={status} className={cn('rounded-lg border bg-muted/20', cfg.topBorder)}>
                      {/* Column header */}
                      <div className="px-3 py-2 border-b flex items-center justify-between">
                        <span className="text-sm font-semibold">{cfg.label}</span>
                        <Badge variant="secondary" className="text-xs">{cols.length}</Badge>
                      </div>

                      {/* Order cards */}
                      <div className="p-2 space-y-2 min-h-[80px]">
                        {cols.length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-4">—</p>
                        )}
                        {cols.map((order: any) => {
                          const tech     = getTech(order.technician_id);
                          const stageRef = status === 'building' ? order.started_at : order.created_at;
                          return (
                            <div
                              key={order.id}
                              className="bg-background rounded-md border p-3 space-y-2 shadow-sm cursor-pointer hover:border-primary/40 transition-colors"
                              onClick={() => { setOrderToView(order); setDetailTab('components'); setDetailDialogOpen(true); }}
                            >
                              {/* Order number + days */}
                              <div className="flex items-center justify-between">
                                <span className="font-mono text-xs font-bold">{order.order_number || '—'}</span>
                                <span className="text-xs text-muted-foreground">{daysAgo(stageRef)}</span>
                              </div>

                              {/* Build SKU + type */}
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium leading-tight">{order.build_sku}</p>
                                {order.build_type && order.build_type !== 'Stock' && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">{order.build_type}</span>
                                )}
                              </div>

                              {/* Qty + technician */}
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span className="flex items-center gap-1.5">
                                  {order.quantity} {order.quantity === 1 ? 'unit' : 'units'}
                                  <PausedBadge pauses={openPauses.filter(p => p.assembly_order_id === order.id)} quantity={order.quantity} />
                                </span>
                                {tech ? (
                                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary font-bold text-xs" title={tech.name}>
                                    {tech.initials || tech.name?.[0]}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground/50 italic">Unassigned</span>
                                )}
                              </div>

                              {/* Checklist progress — where inside the stage each unit is */}
                              {status !== 'pending' && (
                                <ChecklistProgress checklistProgress={order.checklist_progress} quantity={order.quantity} />
                              )}

                              {/* TO number if set */}
                              {order.to_number && (
                                <p className="text-xs text-muted-foreground font-mono">TO: {order.to_number}</p>
                              )}

                              {/* Serial numbers */}
                              {(() => {
                                const sns = order.serial_numbers as Record<string, string> | null;
                                const entries = sns ? Object.values(sns).filter(Boolean) : [];
                                if (entries.length > 0) {
                                  return <p className="text-xs text-muted-foreground font-mono">S/N: {entries.join(', ')}</p>;
                                }
                                if (order.serial_number) {
                                  return <p className="text-xs text-muted-foreground font-mono">S/N: {order.serial_number}</p>;
                                }
                                return null;
                              })()}

                              {/* Notes */}
                              {order.notes && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 truncate" title={order.notes}>{order.notes}</p>
                              )}

                              {/* Action */}
                              {cfg.next ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full h-7 text-xs"
                                  disabled={advanceStatusMutation.isPending}
                                  onClick={(e) => { e.stopPropagation(); advanceStatusMutation.mutate({ orderId: order.id, nextStatus: cfg.next!, orderNumber: order.order_number, previousStatus: order.status }); }}
                                >
                                  {cfg.nextLabel}
                                  <ArrowRight className="h-3 w-3 ml-1" />
                                </Button>
                              ) : (
                                <div className="space-y-1">
                                  {canReview && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full h-7 text-xs"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOrderToReview(order);
                                        setReviewDialogOpen(true);
                                      }}
                                    >
                                      <ClipboardCheck className="h-3 w-3 mr-1" />
                                      Review
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    className="w-full h-7 text-xs bg-green-600 hover:bg-green-700"
                                    onClick={(e) => { e.stopPropagation(); setApproveModal({ order }); }}
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Post
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ── Create Order Modal ─────────────────────────────────────────────── */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Create Assembly Order</CardTitle>
                <button onClick={() => setCreateModal(null)} className="text-muted-foreground hover:text-foreground">✕</button>
              </div>
              <p className="text-sm text-muted-foreground">
                {createModal.reservation.reservation_code} · <span className="font-mono">{createModal.reservation.build_sku}</span>
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Order Number <span className="text-red-500">*</span></label>
                <div className="relative mt-1">
                  <Input
                    value={createModal.form.order_number}
                    onChange={e => setCreateModal(m => m && ({ ...m, form: { ...m.form, order_number: e.target.value } }))}
                    className="font-mono text-transparent caret-foreground"
                  />
                  <div className="absolute inset-0 flex items-center px-3 pointer-events-none select-none font-mono text-sm">
                    <span>{createModal.form.order_number}</span>
                    <span className="text-muted-foreground/30">{'##'.slice(0, Math.max(0, 11 - createModal.form.order_number.length))}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Technician</label>
                <select
                  value={createModal.form.technician_id}
                  onChange={e => setCreateModal(m => m && ({ ...m, form: { ...m.form, technician_id: e.target.value } }))}
                  className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">— Unassigned —</option>
                  {(technicians as any[]).map(t => (
                    <option key={t.id} value={t.id}>{t.name}{t.surname ? ' ' + t.surname : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                {(() => {
                  const fulfilled = createModal.reservation.quantity_fulfilled || 0;
                  const activeOrdered = createModal.reservation._activeOrdered || 0;
                  const maxAllowed = Math.max(1, createModal.reservation.quantity - fulfilled - activeOrdered);
                  return (
                    <>
                      <label className="text-sm font-medium">
                        Quantity
                        <span className="text-muted-foreground font-normal ml-2">
                          (max {maxAllowed} remaining)
                        </span>
                      </label>
                      <Input
                        type="number"
                        min={1}
                        max={maxAllowed}
                        value={createModal.form.quantity}
                        onChange={e => {
                          const val = Math.min(Math.max(1, parseInt(e.target.value) || 1), maxAllowed);
                          setCreateModal(m => m && ({ ...m, form: { ...m.form, quantity: val } }));
                        }}
                        className="mt-1"
                      />
                    </>
                  );
                })()}
              </div>

              <div>
                <label className="text-sm font-medium">Build Type</label>
                <select
                  value={createModal.form.build_type}
                  onChange={e => {
                    const bt = e.target.value;
                    setCreateModal(m => {
                      if (!m) return m;
                      const notes = bt === 'Demo' && !m.form.notes
                        ? 'Demo PC — follow setup checklist in order notes'
                        : m.form.notes;
                      return { ...m, form: { ...m.form, build_type: bt, notes } };
                    });
                  }}
                  className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="Stock">Stock</option>
                  <option value="Custom">Custom</option>
                  <option value="Demo">Demo</option>
                  <option value="Other">Other</option>
                </select>

                {/* Demo setup instructions panel */}
                {createModal.form.build_type === 'Demo' && (
                  <div className="mt-2">
                    <DemoSetupBanner compact />
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">TO Number <span className="text-muted-foreground font-normal">(optional)</span></label>
                <Input
                  value={createModal.form.to_number}
                  onChange={e => setCreateModal(m => m && ({ ...m, form: { ...m.form, to_number: e.target.value.toUpperCase() } }))}
                  className="mt-1 font-mono uppercase"
                  placeholder="e.g. TO-12345"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Notes <span className="text-muted-foreground font-normal">(optional)</span></label>
                <Input
                  value={createModal.form.notes}
                  onChange={e => setCreateModal(m => m && ({ ...m, form: { ...m.form, notes: e.target.value } }))}
                  className="mt-1"
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setCreateModal(null)}>Cancel</Button>
                <Button
                  disabled={createOrderMutation.isPending || createModal.form.quantity < 1 || !createModal.form.order_number.trim()}
                  onClick={() => createOrderMutation.mutate({
                    order_number:   createModal.form.order_number.trim(),
                    reservation_id: createModal.reservation.id,
                    build_id:       createModal.reservation.build_id || null,
                    build_sku:      createModal.reservation.build_sku,
                    technician_id:  createModal.form.technician_id || null,
                    quantity:       createModal.form.quantity,
                    build_type:     createModal.form.build_type,
                    to_number:      createModal.form.to_number,
                    notes:          createModal.form.notes,
                  })}
                >
                  {createOrderMutation.isPending ? 'Creating…' : 'Create Order'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Pending Parts Dialog ─────────────────────────────────────────── */}
      <Dialog open={pendingPartsView !== null} onOpenChange={open => { if (!open) setPendingPartsView(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <span className={cn('inline-block h-2.5 w-2.5 rounded-full', pendingPartsView === 'toOrder' ? 'bg-red-500' : 'bg-amber-400')} />
              {pendingPartsView === 'toOrder' ? 'Parts to Order' : 'Parts Awaiting Delivery'}
            </DialogTitle>
            <DialogDescription>
              {pendingPartsView === 'toOrder'
                ? 'Shortfall on open reservations with no purchase order raised.'
                : 'Shortfall on open reservations that an open purchase order will cover.'}
              {pendingParts.length > 0 && (
                <> {pendingParts.length} {pendingParts.length === 1 ? 'part' : 'parts'},{' '}
                  {pendingParts.reduce((n, p) => n + p.total, 0)} pcs.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 -mx-1 px-1 space-y-3">
            {!stockReadiness ? (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : pendingParts.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">No parts in this state</p>
            ) : (
              <div className="border rounded-lg divide-y">
                {pendingParts.map(part => (
                  <div key={part.sku} className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {part.componentType && (
                            <Badge variant="outline" className="text-[10px]">{getComponentTypeLabel(part.componentType as ComponentType)}</Badge>
                          )}
                          <Link
                            to={`/components/${encodeURIComponent(part.sku)}`}
                            className="font-mono text-xs font-medium text-primary hover:underline"
                          >{part.sku}</Link>
                        </div>
                        {part.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate" title={part.description}>{part.description}</p>
                        )}
                      </div>
                      <span className={cn('text-sm font-bold tabular-nums shrink-0', pendingPartsView === 'toOrder' ? 'text-red-600' : 'text-amber-600')}>
                        {part.total} pcs
                      </span>
                    </div>

                    <ul className="space-y-1 border-l-2 border-muted pl-3">
                      {part.reservations.map(({ reservationId, qty }) => {
                        const r = reservationById.get(reservationId);
                        const aos = inFlightByReservation[reservationId] ?? [];
                        return (
                          <li key={reservationId} className="flex items-center gap-x-2 gap-y-1 flex-wrap text-xs">
                            <span className="tabular-nums text-muted-foreground w-10 shrink-0">{qty} pcs</span>
                            <Link to={`/reservations/${reservationId}`} className="font-mono font-medium text-primary hover:underline">
                              {r?.reservation_code ?? 'Reservation'}
                            </Link>
                            {r?.build_sku && (
                              <>
                                <span className="text-muted-foreground">·</span>
                                {r.build_id ? (
                                  <Link to={`/builds/${r.build_id}`} className="font-mono text-primary hover:underline" title={r.build_description || undefined}>
                                    {r.build_sku}
                                  </Link>
                                ) : (
                                  <span className="font-mono" title={r.build_description || undefined}>{r.build_sku}</span>
                                )}
                              </>
                            )}
                            {aos.map((o: any) => (
                              <button
                                key={o.id}
                                type="button"
                                onClick={() => {
                                  setPendingPartsView(null);
                                  setOrderToView(o);
                                  setDetailTab('components');
                                  setDetailDialogOpen(true);
                                }}
                                title={`${STATUS_CONFIG[o.status as PipelineStatus]?.label ?? o.status} · ${o.quantity} ${o.quantity === 1 ? 'unit' : 'units'}`}
                                className={cn('rounded px-1.5 py-0.5 font-mono text-[11px] hover:ring-1 hover:ring-current transition-shadow', STATUS_CONFIG[o.status as PipelineStatus]?.badge ?? 'bg-muted')}
                              >
                                {o.order_number}
                              </button>
                            ))}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {pendingPartsView === 'awaiting' && reservationsWithoutComponents.length > 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Also counted as awaiting, with no component list to break down:{' '}
                {reservationsWithoutComponents.map((r: any, i: number) => (
                  <span key={r.id}>
                    {i > 0 && ', '}
                    <Link to={`/reservations/${r.id}`} className="font-mono font-medium underline">{r.reservation_code}</Link>
                  </span>
                ))}
              </p>
            )}
          </div>

          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setPendingPartsView(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Post Confirmation Modal ─────────────────────────────────────────── */}
      {/* ── Review Dialog ────────────────────────────────────────────────── */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="flex flex-col max-h-[90vh]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Review Assembly Order</DialogTitle>
            <DialogDescription>Review and post this build order.</DialogDescription>
          </DialogHeader>

          {orderToReview && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-1">
              <div className="p-3 bg-muted rounded-lg space-y-1">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Order:</span>
                  <span className="font-mono text-sm font-medium">{orderToReview.order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Build:</span>
                  <span className="text-sm">{orderToReview.build_sku || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Technician:</span>
                  <span className="text-sm">{getTech(orderToReview.technician_id)?.name || 'Unassigned'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Quantity:</span>
                  <span className="text-sm">{orderToReview.quantity} {orderToReview.quantity === 1 ? 'unit' : 'units'}</span>
                </div>
                {orderToReview.to_number && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Transfer Order:</span>
                    <span className="font-mono text-sm font-medium">{orderToReview.to_number}</span>
                  </div>
                )}
                {(() => {
                  const sns = orderToReview.serial_numbers as Record<string, string> | null;
                  const entries = sns ? Object.entries(sns).filter(([, v]) => v) : [];
                  if (entries.length > 1) {
                    return (
                      <div>
                        <span className="text-sm text-muted-foreground">Serial Numbers:</span>
                        <div className="mt-1 space-y-0.5">
                          {entries.map(([unit, sn]) => (
                            <div key={unit} className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Unit {unit}:</span>
                              <span className="font-mono text-sm font-medium">{sn}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (entries.length === 1) {
                    return (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Serial Number:</span>
                        <span className="font-mono text-sm font-medium">{entries[0][1]}</span>
                      </div>
                    );
                  }
                  if (orderToReview.serial_number) {
                    return (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Serial Number:</span>
                        <span className="font-mono text-sm font-medium">{orderToReview.serial_number}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {orderToReview.notes && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Technician Notes</p>
                  <p className="text-sm p-3 bg-yellow-50 border border-yellow-200 rounded-md whitespace-pre-wrap">{orderToReview.notes}</p>
                </div>
              )}

              {/* Stage Timeline */}
              <ReviewBuildTimeline
                stages={review.stages}
                duration={review.duration}
                lunchMs={review.lunchMs}
                pendingPauses={review.unreviewedPauses}
              />

              {/* Pauses: each approved or rejected before posting */}
              <PauseReviewList
                order={orderToReview}
                pauses={review.pauses}
                canReview
                activeDays={review.activeDays}
              />

              {/* Who built each unit, who submitted and signed */}
              <BuildAttribution order={orderToReview} />

              {/* AO Notes */}
              {reviewNotes.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notes</p>
                  <div className="space-y-2">
                    {reviewNotes.map((n: any) => (
                      <div key={n.id} className="p-2 bg-muted rounded-md">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium">{n.author?.alias || n.author?.name || '—'}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(n.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{n.note_text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {orderToReview.tech_signature_url && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Technician Signature</p>
                  <img src={orderToReview.tech_signature_url} alt="Tech signature" className="border rounded-md max-h-20 bg-white" />
                </div>
              )}

              {orderToReview.manager_signature_url && !showManagerSignoff ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Your Signature</p>
                  <img src={orderToReview.manager_signature_url} alt="Manager signature" className="border rounded-md max-h-20 bg-white" />
                  <button className="text-xs text-primary underline" onClick={() => setShowManagerSignoff(true)}>Re-sign</button>
                </div>
              ) : showManagerSignoff ? (
                <SignaturePad
                  aoId={orderToReview.id}
                  type="manager"
                  signedBy={currentUser?.technicianId || ''}
                  onSigned={(url, signedAt) => {
                    // See AssemblyOrdersPage - orderToReview is a snapshot, so
                    // the signature has to be merged in for it to render now.
                    setOrderToReview((prev: any) =>
                      prev ? { ...prev, manager_signature_url: url, manager_signed_at: signedAt } : prev
                    );
                    setShowManagerSignoff(false);
                    queryClient.invalidateQueries({ queryKey: ['production-orders'] });
                  }}
                  onCancel={() => setShowManagerSignoff(false)}
                />
              ) : (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Manager / QC Sign-Off</p>
                  <Button variant="outline" size="sm" onClick={() => setShowManagerSignoff(true)}>Sign Off</Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-shrink-0 pt-2 border-t">
            {orderToReview && (
              <PrintZebraLabels
                orderId={orderToReview.id}
                orderNumber={orderToReview.order_number}
                sku={orderToReview.build_sku || ''}
                quantity={orderToReview.quantity || 1}
                serialNumber={orderToReview.serial_number}
                serialNumbers={orderToReview.serial_numbers}
                onSerialsUpdated={() => queryClient.invalidateQueries({ queryKey: ['production-orders'] })}
              />
            )}
            <Button variant="outline" onClick={() => { setReviewDialogOpen(false); setShowManagerSignoff(false); }}>Cancel</Button>
            {review.unreviewedPauses > 0 && (
              <span className="mr-auto self-center text-xs text-amber-700 dark:text-amber-500">
                Approve or reject {review.unreviewedPauses === 1 ? 'the pause' : `all ${review.unreviewedPauses} pauses`} to post.
              </span>
            )}
            <Button
              className="bg-green-600 hover:bg-green-700"
              disabled={approveOrderMutation.isPending || review.unreviewedPauses > 0}
              onClick={() => {
                if (!orderToReview) return;
                approveOrderMutation.mutate({ order: orderToReview });
                setReviewDialogOpen(false);
                setShowManagerSignoff(false);
              }}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {approveOrderMutation.isPending ? 'Posting…' : 'Review & Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Order Detail Dialog ──────────────────────────────────────────── */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {enrichedOrder?.order_number || orderToView?.order_number || '—'} — {enrichedOrder?.build?.sku || orderToView?.build_sku || 'Assembly Order'}
            </DialogTitle>
            <DialogDescription>{enrichedOrder?.build?.description || 'Build order details'}</DialogDescription>
          </DialogHeader>

          {enrichedOrder ? (
            <div className="space-y-4">
              {/* Order summary */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-muted rounded-lg text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">Order</span>
                  <p className="font-mono font-medium">{enrichedOrder.order_number}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Build Type</span>
                  <p>{enrichedOrder.build_type || 'Stock'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Quantity</span>
                  <p>{enrichedOrder.quantity || 1} unit{(enrichedOrder.quantity || 1) > 1 ? 's' : ''}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Reservation</span>
                  <p className="font-mono">
                    {enrichedOrder.reservation_id && enrichedOrder.reservation?.reservation_code ? (
                      <Link to={`/reservations/${enrichedOrder.reservation_id}`} className="text-primary hover:underline">
                        {enrichedOrder.reservation.reservation_code}
                      </Link>
                    ) : '—'}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Technician</span>
                  <p>{enrichedOrder.technician?.name || 'Unassigned'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Transfer Order</span>
                  <p className="font-mono">{enrichedOrder.to_number || '—'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Serial Number{((enrichedOrder.serial_numbers as Record<string, string> | null) && Object.values(enrichedOrder.serial_numbers as Record<string, string>).filter(Boolean).length > 1) ? 's' : ''}</span>
                  {(() => {
                    const sns = enrichedOrder.serial_numbers as Record<string, string> | null;
                    const entries = sns ? Object.entries(sns).filter(([, v]) => v) : [];
                    if (entries.length > 1) {
                      return (
                        <div className="space-y-0.5">
                          {entries.map(([unit, sn]) => (
                            <p key={unit} className="font-mono text-sm">
                              <span className="text-muted-foreground text-xs">#{unit}</span> {sn}
                            </p>
                          ))}
                        </div>
                      );
                    }
                    if (entries.length === 1) {
                      return <p className="font-mono">{entries[0][1]}</p>;
                    }
                    return <p className="font-mono">{enrichedOrder.serial_number || '—'}</p>;
                  })()}
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Status</span>
                  <Badge className={STATUS_CONFIG[enrichedOrder.status as PipelineStatus]?.badge || 'bg-gray-100 text-gray-700'}>
                    {STATUS_CONFIG[enrichedOrder.status as PipelineStatus]?.label || enrichedOrder.status}
                  </Badge>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Created</span>
                  <p>{formatDate(enrichedOrder.created_at)}</p>
                </div>
                {enrichedOrder.started_at && (
                  <div>
                    <span className="text-xs text-muted-foreground">Started</span>
                    <p>{formatDate(enrichedOrder.started_at)}</p>
                  </div>
                )}
              </div>

              {/* Workflow stepper */}
              {(() => {
                const FLOW_STATUSES = PIPELINE_STATUSES;
                const FLOW_ICONS: Record<PipelineStatus, any> = {
                  pending: Clock,
                  building: Wrench,
                  software: Monitor,
                  testing: TestTube,
                  awaiting_review: ClipboardCheck,
                };
                const FLOW_LABELS: Record<PipelineStatus, string> = {
                  pending: 'Pending',
                  building: 'Building',
                  software: 'Software',
                  testing: 'Testing',
                  awaiting_review: 'Review',
                };
                const currentStatus = enrichedOrder.status as PipelineStatus;
                const currentIdx = FLOW_STATUSES.indexOf(currentStatus);
                const isCompleted = enrichedOrder.status === 'completed';

                return (
                  <div className="flex items-center gap-1 px-2">
                    {FLOW_STATUSES.map((step, idx, arr) => {
                      const StepIcon = FLOW_ICONS[step];
                      const stepIdx = idx;
                      const isActive = step === currentStatus;
                      const isDone = isCompleted || stepIdx < currentIdx;

                      return (
                        <div key={step} className="flex items-center flex-1">
                          <div className="flex flex-col items-center gap-1 flex-1">
                            <div className={cn(
                              'w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all',
                              isActive
                                ? `${STATUS_CONFIG[step]?.badge || 'bg-gray-100 text-gray-700'} border-current shadow-sm ring-2 ring-offset-1 ring-current`
                                : isDone
                                  ? 'bg-green-100 text-green-700 border-green-300'
                                  : 'bg-muted text-muted-foreground border-border'
                            )}>
                              {isDone && !isActive ? <CheckCircle2 className="h-4 w-4" /> : <StepIcon className="h-4 w-4" />}
                            </div>
                            <span className={cn(
                              'text-[10px] leading-tight text-center font-medium',
                              isActive ? 'text-foreground' : 'text-muted-foreground'
                            )}>
                              {FLOW_LABELS[step]}
                            </span>
                          </div>
                          {idx < arr.length - 1 && (
                            <div className={cn('h-0.5 flex-1 mx-1 rounded mb-4', isDone ? 'bg-green-300' : 'bg-border')} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Notes */}
              {enrichedOrder.notes && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Technician Notes</p>
                  <p className="text-sm p-3 bg-yellow-50 border border-yellow-200 rounded-md whitespace-pre-wrap">{enrichedOrder.notes}</p>
                </div>
              )}

              {/* Tab switcher */}
              <div className="flex border-b">
                {(['components', 'checklist', 'notes'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setDetailTab(tab)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
                      detailTab === tab
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              {detailTab === 'components' && (
                <>
                  {loadingComponents ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    </div>
                  ) : (detailComponents?.length ?? 0) > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-3 font-medium">Type</th>
                            <th className="text-left p-3 font-medium">Part Number</th>
                            <th className="text-left p-3 font-medium">Description</th>
                            <th className="text-right p-3 font-medium">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailComponents!.map((comp, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="p-3 text-xs"><Badge variant="outline">{comp.typeLabel}</Badge></td>
                              <td className="p-3 font-mono text-xs">{comp.sku}</td>
                              <td className="p-3 text-xs text-muted-foreground">{comp.description || '—'}</td>
                              <td className="p-3 text-right">{comp.qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No components found for this order</p>
                    </div>
                  )}
                </>
              )}

              {detailTab === 'checklist' && (
                <AssemblyChecklist
                  order={enrichedOrder}
                  readOnly
                />
              )}

              {detailTab === 'notes' && currentUser?.technicianId && (
                <AONotes aoId={enrichedOrder.id} currentUserId={currentUser.technicianId} />
              )}
            </div>
          ) : orderToView ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : null}

          <DialogFooter>
            {enrichedOrder && (
              <PrintZebraLabels
                orderId={enrichedOrder.id}
                orderNumber={enrichedOrder.order_number}
                sku={enrichedOrder.build?.sku || enrichedOrder.build_sku || ''}
                description={enrichedOrder.build?.description}
                quantity={enrichedOrder.quantity || 1}
                serialNumber={enrichedOrder.serial_number}
                serialNumbers={enrichedOrder.serial_numbers}
                onSerialsUpdated={() => queryClient.invalidateQueries({ queryKey: ['production-order-detail', enrichedOrder.id] })}
              />
            )}
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {approveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Post Order</CardTitle>
                <button onClick={() => setApproveModal(null)} className="text-muted-foreground hover:text-foreground">✕</button>
              </div>
              <p className="text-sm text-muted-foreground">
                <span className="font-mono font-medium">{approveModal.order.order_number}</span>
                {' · '}{approveModal.order.build_sku}
                {' · '}{approveModal.order.quantity} {approveModal.order.quantity === 1 ? 'unit' : 'units'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                This will mark the order as Posted and update the linked reservation's fulfilment count.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setApproveModal(null)}>Cancel</Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  disabled={approveOrderMutation.isPending}
                  onClick={() => approveOrderMutation.mutate({ order: approveModal.order })}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {approveOrderMutation.isPending ? 'Posting…' : 'Post'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}
